import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { concat, first, interval, race, timer } from 'rxjs';

/**
 * Keeps the installed app in sync with the deployed build.
 *
 * The Angular service worker only compares ngsw.json with its cache when asked to. Without this
 * service a tab that stays open never learns about a new deploy, and a plain F5 still serves the
 * old cached bundle. So we ask regularly (timer, tab becoming visible, API 404/405 via the
 * VersionCheckInterceptor) and, as soon as a new version is fully downloaded, activate it and
 * reload the page.
 */
@Injectable({
  providedIn: 'root'
})
export class PwaUpdateService {

  private static readonly POLL_INTERVAL_MS = 5 * 60 * 1000;
  private static readonly MIN_CHECK_GAP_MS = 60 * 1000;

  private lastCheck = 0;

  constructor(
    private updates: SwUpdate,
    private appRef: ApplicationRef
  ) {
    if (!updates.isEnabled) {
      return;
    }

    updates.versionUpdates.subscribe(evt => {
      switch (evt.type) {
        case 'VERSION_READY':
          console.log(`New app version ready (${evt.latestVersion.hash}), reloading.`);
          updates.activateUpdate().then(() => document.location.reload());
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.warn(`Failed to install app version '${evt.version.hash}': ${evt.error}`);
          break;
      }
    });

    // The cache is broken (e.g. a deploy happened while a file was still being fetched); a reload
    // fetches everything fresh from the server.
    updates.unrecoverable.subscribe(() => document.location.reload());

    // Poll once the app is stable (or after 30 s, matching the SW registration strategy, in case
    // long-running timers keep the app from ever becoming stable), then every POLL_INTERVAL_MS.
    const appIsStable$ = race(this.appRef.isStable.pipe(first(isStable => isStable)), timer(30000));
    concat(appIsStable$, interval(PwaUpdateService.POLL_INTERVAL_MS))
      .subscribe(() => this.checkForUpdate());

    // A tab left open overnight should update as soon as the user comes back to it.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkForUpdate();
      }
    });
  }

  /** Asks the service worker to compare ngsw.json with its cache. Throttled to one check per minute. */
  checkForUpdate(): void {
    if (!this.updates.isEnabled) {
      return;
    }
    const now = Date.now();
    if (now - this.lastCheck < PwaUpdateService.MIN_CHECK_GAP_MS) {
      return;
    }
    this.lastCheck = now;
    this.updates.checkForUpdate().catch(err => console.warn('Update check failed', err));
  }
}
